import { Userpilot } from "userpilot";

import { getCspNonce } from "@av/config/csp-nonce";

import type {
  TrackedCompany,
  TrackedUser,
  TrackingAdapter,
  TrackingEvents,
} from "../TrackingAdapter";

export class UserPilotAdapter implements TrackingAdapter {
  private initialized = false;

  initialize(token: string) {
    const cspNonce = getCspNonce();
    const settings = cspNonce ? { nonce: cspNonce } : undefined;

    Userpilot.initialize(token, settings);
    this.initialized = true;
  }

  onLogin(user: TrackedUser, company: TrackedCompany) {
    if (this.initialized) {
      Userpilot.identify(user.id, {
        name: user.name,
        company: company,
      });
    }
  }

  onPageChange(url: string) {
    if (this.initialized) {
      Userpilot.reload({ url });
    }
  }

  onEvent(type: TrackingEvents, properties: Record<string, string>) {
    if (this.initialized) {
      Userpilot.track(type, properties);
    }
  }

  clearWebStorage() {
    this.initialized = false;

    try {
      Userpilot.clearQueue();
      Userpilot.clean();
      Userpilot.destroy();
    } catch (error) {
      console.warn("Unable to destroy user pilot session", error);
    }

    /**
     * Userpilot storage is expected while a user is logged in. At auth boundaries we remove only
     * Userpilot-owned web-storage keys so they do not survive logout or leak into the next user.
     */
    try {
      Object.keys(window.localStorage)
        .filter((key) => /userpilot/i.test(key))
        .forEach((key) => window.localStorage.removeItem(key));
      Object.keys(window.sessionStorage)
        .filter((key) => /userpilot/i.test(key))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch (error) {
      console.warn("Unable to clear storage keys", error);
    }
  }
}

export const userPilotAdapter = new UserPilotAdapter();
